import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import { UserAvatar } from '@/components/ui/UserAvatar'
import {
  getRespondentRole,
  type StudyChatProfile,
} from './respondentProfileText'

interface QuotesChatInvitationProps {
  profiles: Array<StudyChatProfile>
  selectionSeed: string
  onOpenChat: () => void
}

function respondentRank(key: string): number {
  let hash = 2166136261
  for (const character of key) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619)
  return hash >>> 0
}

function portraitUrl(profile: StudyChatProfile): string | undefined {
  if (profile.image_url?.trim()) {
    return profile.image_url
  }
  const attrs = profile.respondent_attributes
  if (!attrs) {
    return undefined
  }
  for (const key of ['image_url', 'imageUrl']) {
    const value = attrs[key]
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }
  return undefined
}

function Portrait({ profile }: { profile: StudyChatProfile }) {
  const [hidden, setHidden] = useState(false)
  const { displayUrl, handleImageError } = usePersonaThumbFallback(portraitUrl(profile), 64)

  if (!displayUrl || hidden) {
    const name = profile.name || 'Twin'
    const initials = name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
    return <UserAvatar initials={initials} name={name} size="sm" />
  }

  return (
    <img
      key={displayUrl}
      src={displayUrl}
      alt=""
      className="h-9 w-9 rounded-full object-cover"
      onError={() => handleImageError(() => setHidden(true))}
    />
  )
}

export function ChatInvitationSkeleton() {
  return (
    <div
      data-testid="summary-quotes-chat-skeleton"
      aria-busy="true"
      className="flex w-full flex-col items-start gap-4 py-2"
    >
      <Skeleton className="h-4 w-4/5" />
      <ul className="w-full divide-y divide-border/60">
        {[0, 1, 2].map((index) => (
          <li key={index} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
            <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Skeleton className="h-3.5 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </li>
        ))}
      </ul>
      <Skeleton className="h-8 w-36 rounded-full" />
    </div>
  )
}

export function QuotesChatInvitation({ profiles, selectionSeed, onOpenChat }: QuotesChatInvitationProps) {
  const { t } = useTranslation('app')
  // Stable sampling keeps the same three people visible as the report rerenders.
  const respondents = profiles
    .map((profile, index) => ({ profile, index, rank: respondentRank(`${selectionSeed}:${profile.respondent_id ?? index}`) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, 3)

  return (
    <div data-testid="summary-quotes-chat-invitation" className="flex w-full flex-col items-start gap-4 py-2">
      {/* Same face as the Featured question line (`SummaryCardQuestions`):
          body sans via Text, small, medium. A heading would pick up the
          display font. */}
      <Text as="p" size="sm" weight="medium">
        {t('studyReport.emptyQuotesChatHeading')}
      </Text>
      <ul className="w-full divide-y divide-border/60">
        {respondents.map(({ profile, index }) => {
          const role = getRespondentRole(profile)
          return (
            <li
              key={profile.respondent_id || profile.name || index}
              data-testid={`summary-chat-respondent-${profile.respondent_id ?? index}`}
              className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
            >
              <span className="shrink-0" aria-hidden="true"><Portrait profile={profile} /></span>
              <div className="flex min-w-0 flex-col gap-0.5">
                <Text as="p" size="sm" weight="medium" className="truncate text-foreground">
                  {profile.name}
                </Text>
                {role && <Text as="p" size="xs" color="muted" className="line-clamp-1">{role}</Text>}
              </div>
            </li>
          )
        })}
      </ul>
      <Button type="button" variant="secondary" size="sm" onClick={onOpenChat} className="rounded-full">
        {t('studyReport.emptyQuotesChatAction')}
        <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  )
}
